import type {
  Schedule,
  ScheduleMode,
  ScheduleUnit,
  SchedulePeriod,
  BannerLevel,
  ValidationResult,
  CalendarCell,
  TimelineTick,
  Preset,
  ModeOption,
  ProductItem,
  DayOfWeek,
  SchedulePatch,
} from "../types";

export type { CalendarCell, TimelineTick };

export const DOW: readonly string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MON: readonly string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const UNIT_H: Record<ScheduleUnit, number> = { hours: 1, days: 24, weeks: 168 };
const PERIOD_DAYS: Record<SchedulePeriod, number> = { day: 1, week: 7, month: 30 };

export const DEFAULT_MAX_DAYS = 7;

export const MODES: readonly ModeOption[] = [
  { id: "every", label: "Every", icon: "M17 2l3 3-3 3M4 11V9a4 4 0 0 1 4-4h16M7 22l-3-3 3-3M20 13v2a4 4 0 0 1-4 4H4" },
  { id: "times", label: "Times per", icon: "M6 5v14M10 5v14M14 5v14M18 5v14M3 16l18-8" },
  { id: "weekdays", label: "Days of week", icon: "M4 6h16v14H4zM4 10h16M8 3v4M16 3v4M8 14h2M14 14h2M8 17h2" },
  { id: "prn", label: "As needed", icon: "M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" },
];

export const UNIT_OPTIONS: readonly ScheduleUnit[] = ["hours", "days", "weeks"];
export const PERIOD_OPTIONS: readonly SchedulePeriod[] = ["day", "week", "month"];
export const WEEKDAY_ORDER: readonly DayOfWeek[] = [1, 2, 3, 4, 5, 6, 0];

export const PRESETS: readonly Preset[] = [
  { label: "Daily", patch: { mode: "every", every: 1, unit: "days" } },
  { label: "Once a week", patch: { mode: "times", times: 1, period: "week" } },
  { label: "2\u00d7 a week", patch: { mode: "times", times: 2, period: "week" } },
  { label: "3\u00d7 a week", patch: { mode: "times", times: 3, period: "week" } },
  { label: "Every 2 days", patch: { mode: "every", every: 2, unit: "days" } },
  { label: "Every 3 days", patch: { mode: "every", every: 3, unit: "days" } },
  { label: "As needed", patch: { mode: "prn" } },
];

export const unitWord = (u: ScheduleUnit): string => (u === "hours" ? "hour" : u === "days" ? "day" : "week");

export const plural = (n: number, w: string): string => `${n} ${n === 1 ? w : `${w}s`}`;

export const listJoin = (a: string[]): string => (a.length <= 1 ? a.join("") : `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}`);

export const monFirst = (a: number, b: number): number => ((a + 6) % 7) - ((b + 6) % 7);

export const sortedDays = (days: DayOfWeek[] = []): DayOfWeek[] => [...days].sort(monFirst) as DayOfWeek[];

export const clampInt = (v: string | number, lo: number, hi: number): number => {
  const n = parseInt(String(v), 10);
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
};

export const fmtDate = (d: Date): string => `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`;

export const fmtTime = (d: Date): string => {
  const h = d.getHours();
  const m = d.getMinutes();
  return `${h % 12 || 12}:${m < 10 ? "0" : ""}${m} ${h < 12 ? "AM" : "PM"}`;
};

export const fmtHour = (startDate: Date, h: number): string => {
  const d = new Date(startDate);
  d.setHours(Math.floor(h), Math.round((h % 1) * 60), 0, 0);
  return fmtTime(d);
};

export const gapHours = (f: Schedule): number | null => {
  if (f.mode === "unset") return null;
  if (f.mode === "every") return f.every * UNIT_H[f.unit];
  if (f.mode === "times") return (PERIOD_DAYS[f.period] * 24) / f.times;
  if (f.mode === "weekdays") {
    const d = sortedDays(f.days);
    if (!d.length) return null;
    let g = 0;
    for (let i = 0; i < d.length; i++) {
      let gap = (d[(i + 1) % d.length] - d[i] + 7) % 7;
      if (gap === 0) gap = 7;
      if (gap > g) g = gap;
    }
    return g * 24;
  }
  return f.prnCap ? f.prnMax * 24 : null;
};

export const getCounts = (f: Schedule, startDate: Date): number[] => {
  const c = new Array<number>(14).fill(0);
  if (f.mode === "every") {
    const step = f.every * UNIT_H[f.unit];
    for (let h = 0; h < 14 * 24; h += step) {
      const di = Math.floor((startDate.getHours() + h) / 24);
      if (di < 14) c[di]++;
    }
  } else if (f.mode === "times") {
    const span = PERIOD_DAYS[f.period];
    if (span === 1) {
      for (let a = 0; a < 14; a++) c[a] = f.times;
    } else {
      for (let k = 0; k < 400; k++) {
        const dd = Math.floor((k * span) / f.times);
        if (dd >= 14) break;
        c[dd]++;
      }
    }
  } else if (f.mode === "weekdays") {
    for (let j = 0; j < 14; j++) {
      if (f.days.includes(((startDate.getDay() + j) % 7) as DayOfWeek)) c[j] = 1;
    }
  }
  return c;
};

export const getSummary = (f: Schedule): string => {
  if (f.mode === "unset") return "No dressing change frequency selected";
  if (f.mode === "every") {
    let s = `Change every ${f.every === 1 ? unitWord(f.unit) : plural(f.every, unitWord(f.unit))}`;
    if (f.unit === "hours" && f.every < 24) s += ` (about ${Math.round(240 / f.every) / 10} changes a day)`;
    return s;
  }
  if (f.mode === "times") return `Change ${f.times === 1 ? "once" : f.times === 2 ? "twice" : `${f.times} times`} a ${f.period}`;
  if (f.mode === "weekdays") {
    const d = sortedDays(f.days);
    return d.length ? `Change every ${listJoin(d.map((x) => DOW[x]))}` : "No days selected";
  }
  return `Change as needed${f.prnCap ? `, at least every ${plural(f.prnMax, "day")}` : ""}`;
};

export const getNextText = (f: Schedule, c: number[], startDate: Date): string => {
  if (f.mode === "unset") return "";
  if (f.mode === "prn") return "No fixed schedule \u2014 change when saturated, soiled, or dislodged.";
  if (f.mode === "every" && f.unit === "hours") {
    const out: string[] = [];
    for (let k = 0; k < 4; k++) {
      const t = new Date(startDate.getTime() + k * f.every * 3600000);
      out.push(fmtTime(t) + (t.getDate() !== startDate.getDate() ? ` ${DOW[t.getDay()]}` : ""));
    }
    return `Next: ${out.join(" \u00b7 ")}`;
  }
  const ds: string[] = [];
  for (let i = 0; i < 14 && ds.length < 3; i++) {
    if (c[i] > 0) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      ds.push(fmtDate(d));
    }
  }
  return ds.length ? `Next: ${ds.join(" \u00b7 ")}` : "";
};

export const getDayTicks = (f: Schedule, startDate: Date): TimelineTick[] => {
  const hrs: number[] = [];
  if (f.mode === "every" && f.unit === "hours") {
    for (let h = startDate.getHours(); h < 24; h += f.every) hrs.push(h);
  } else if (f.mode === "times" && f.period === "day") {
    for (let k = 0; k < f.times; k++) hrs.push((startDate.getHours() + (k * 24) / f.times) % 24);
  }
  hrs.sort((a, b) => a - b);
  const showLabels = hrs.length <= 8;
  return hrs.map((h) => {
    const t = fmtHour(startDate, h);
    return {
      hour: h,
      label: t,
      short: showLabels ? t.replace(":00", "").replace(" AM", "a").replace(" PM", "p") : "",
      position: `${(h / 24) * 100}%`,
    };
  });
};

export const presetMatches = (f: Schedule, patch: SchedulePatch): boolean => {
  for (const key of Object.keys(patch) as (keyof SchedulePatch)[]) {
    if (key === "days") {
      const patchDays = patch.days ?? [];
      if (sortedDays(f.days).join() !== sortedDays(patchDays).join()) return false;
    } else if (f[key] !== patch[key]) {
      return false;
    }
  }
  return true;
};

export const checkSchedule = (f: Schedule, maxDays: number): ValidationResult => {
  if (f.mode === "unset") {
    return { level: "info" as BannerLevel, text: "Select a dressing change frequency.", fix: null };
  }
  if (f.mode === "weekdays" && !f.days.length) {
    return { level: "warn" as BannerLevel, text: "Select at least one day.", fix: null };
  }
  const g = gapHours(f);
  if (g === null) {
    return {
      level: "info" as BannerLevel,
      text: `No maximum interval set. Recommended: change within ${plural(maxDays, "day")}.`,
      fix: { label: `Cap at ${maxDays} days`, patch: { prnCap: true, prnMax: maxDays } },
    };
  }
  if (g > maxDays * 24 + 0.01) {
    const gd = g >= 24 ? plural(Math.round(g / 2.4) / 10, "day") : plural(g, "hour");
    return {
      level: "warn" as BannerLevel,
      text: `Longest gap between changes is ${gd} \u2014 over the recommended maximum of ${plural(maxDays, "day")}.`,
      fix: { label: `Use every ${maxDays} days`, patch: { mode: "every", every: maxDays, unit: "days" } },
    };
  }
  return { level: "ok" as BannerLevel, text: `Within recommendation (max ${plural(maxDays, "day")}).`, fix: null };
};

export const getDefaultSchedule = (item: Partial<ProductItem>): Schedule => {
  const savedModes: readonly Exclude<ScheduleMode, "unset">[] = ["every", "times", "weekdays", "prn"];
  const savedMode = savedModes.includes(item?.scheduleMode as Exclude<ScheduleMode, "unset">)
    ? item.scheduleMode as Exclude<ScheduleMode, "unset">
    : null;
  const isFrequencyUnset = item?.frequence === null && item?.frequenceType === null;
  const parsedFrequence = Number(item?.frequence);
  const every = Number.isFinite(parsedFrequence) && parsedFrequence > 0 ? parsedFrequence : 1;
  const unit: ScheduleUnit = item?.frequenceType === "Hrs" ? "hours" : item?.frequenceType === "Weeks" ? "weeks" : "days";
  const times = Number.isFinite(Number(item?.times)) && Number(item?.times) > 0 ? Number(item?.times) : 3;
  const period: SchedulePeriod = item?.period === "day" || item?.period === "month" ? item.period : "week";
  const days = Array.isArray(item?.days)
    ? item.days.filter((day): day is DayOfWeek => Number.isInteger(day) && day >= 0 && day <= 6)
    : [];
  const prnMax = Number.isFinite(Number(item?.prnMax)) && Number(item?.prnMax) > 0 ? Number(item?.prnMax) : 3;
  return {
    mode: savedMode ?? (isFrequencyUnset ? "unset" : "every"),
    every,
    unit,
    times,
    period,
    days,
    prnMax,
    prnCap: item?.prnCap ?? true,
  };
};

export const scheduleToApiFields = (f: Schedule) => {
  const typeMap: Record<ScheduleUnit, string> = { hours: "Hrs", days: "Days", weeks: "Weeks" };
  const emptyFields = {
    frequence: null,
    frequenceType: null,
    times: null,
    period: null,
    days: null,
    prnMax: null,
    prnCap: null,
  };

  if (f.mode === "every") {
    return { ...emptyFields, scheduleMode: "every", frequence: String(f.every), frequenceType: typeMap[f.unit] };
  }
  if (f.mode === "times") {
    return { ...emptyFields, scheduleMode: "times", times: f.times, period: f.period };
  }
  if (f.mode === "weekdays") {
    return { ...emptyFields, scheduleMode: "weekdays", days: f.days };
  }
  if (f.mode === "prn") {
    return { ...emptyFields, scheduleMode: "prn", prnMax: f.prnMax, prnCap: f.prnCap };
  }
  return { ...emptyFields, scheduleMode: null };
};

export const buildCalendarStrip = (schedule: Schedule, counts: number[], startDate: Date, styles: Record<string, string>): CalendarCell[] =>
  counts.map((n, j) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + j);
    const prn = schedule.mode === "prn";
    const due = prn && schedule.prnCap && j > 0 && j % schedule.prnMax === 0;
    const markClass = [styles.calendar_date, n > 0 ? styles.calendar_date_on : due ? styles.calendar_date_due : prn ? styles.calendar_date_prn : ""]
      .filter(Boolean)
      .join(" ");
    return {
      key: j,
      dow: DOW[d.getDay()],
      date: d.getDate(),
      markClass,
      countLabel: n > 1 ? `\u00d7${n}` : due ? "by" : "",
      title: `${fmtDate(d)}: ${due ? "change no later than today" : prn ? "change if needed" : n ? plural(n, "change") : "no change"}`,
    };
  });
